<template>
  <v-container fluid class="pa-0 step-container">
    <v-col cols="12" class="pb-0 mb-n4">
      <v-container fluid class="d-flex pa-0">
        <v-col cols="8" class="d-flex pa-0">
          <v-col cols="5" class="pt-0 pb-0">
            <v-row dense>
              <v-col cols="12">
                <span class="body-2 font-weight-bold">
                  Total amount: ({{ statistics.multimatchesAmount || 0 }})
                </span>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-progress-linear
                  height="10"
                  :value="statistics.multimatchesAmount"
                  :buffer-value="statistics.multimatchesAmount"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="5" class="pt-0 pb-0">
            <v-row dense align="center">
              <v-col cols="12">
                <span class="body-2 font-weight-bold">
                  Resolved: ({{ statistics.resolvedMultimatchesAmount || 0 }})
                </span>
              </v-col>
            </v-row>
            <v-row dense align="center">
              <v-col cols="12">
                <v-progress-linear
                  height="10"
                  color="success"
                  :value="statistics.resolvedMultimatchesAmount"
                  :buffer-value="statistics.multimatchesAmount"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-col>
        <v-col cols="4" class="d-flex justify-end pb-0 pr-0">
          <v-switch
            class="mt-0 mr-1"
            label="Unresolved first"
            @change="getRows"
            v-model="unresolvedFirst"
          />
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small class="pt-1" v-bind="attrs" v-on="on">
                <v-icon color="grey lighten-1">mdi-help-circle-outline</v-icon>
              </v-btn>
            </template>
            <span>
              Resolved multi matches will be shown at the end of the results.
            </span>
          </v-tooltip>
        </v-col>
      </v-container>
    </v-col>
    <VirtualizeTable
      show-pagination
      show-all-option
      group-by="orig_order"
      :items="rows"
      :headers="HeadersList"
      :group-by-headers="GroupByHeadersList"
      :loading="loading"
      :pagination="pagination"
      @onChangeItemsPerPage="handleItemsPerPageChange"
      @onChangePage="handlePageChange"
    >
      <template #migrationPoll.checked="{ item }">
        <v-checkbox
          :disabled="loading || currentStep.status === StepsStatusesMap.SIGNED"
          :input-value="$v(item, 'migrationPoll.checked')"
          @click.native.prevent.stop.capture="
            (event) => openMultimatchResolveDialog(event, item)
          "
        />
      </template>
      <!-- Price -->
      <template #priceInfo.price="{ item }">
        <span class="body-2">${{ $v(item, 'priceInfo.price', 0) }}</span>
      </template>
      <!-- Notes -->
      <template #migrationPoll.notes="{ item }">
        <v-edit-dialog
          v-if="!loading && currentStep.status !== StepsStatusesMap.SIGNED"
        >
          <span class="caption" v-if="!$v(item, 'migrationPoll.notes')">
            Click to type notes...
          </span>
          <span class="caption" v-else>
            {{ $v(item, 'migrationPoll.notes') }}
          </span>
          <template v-slot:input>
            <v-text-field
              counter
              single-line
              label="Edit note"
              :value="$v(item, 'migrationPoll.notes')"
              @change="
                (value) => handleChange(item.id, { notes: value.trim() })
              "
            />
          </template>
        </v-edit-dialog>
        <span class="caption" v-else>
          {{ $v(item, 'migrationPoll.notes') }}
        </span>
      </template>
    </VirtualizeTable>
    <v-snackbar color="success" timeout="1500" v-model="snackbar">
      The row has been updated
    </v-snackbar>
    <ResolveMultimatchDialog
      ref="resolveMultimatchDialog"
      @onResolveMultimatch="handleMultimatchResolve"
    />
  </v-container>
</template>

<script>
import _ from 'lodash';
import VirtualizeTable from '@/components/common/VirtualizeTable';
import { migrationApiV1, processingResultsApiV1 } from '@/api/v1';
import { migrationApiV2, processingResultsApiV2 } from '@/api/v2';
import { DEFAULTS, StepsStatusesMap } from '@/constants';
import ResolveMultimatchDialog from '@/components/migration/dialogs/ResolveMultimatchDialog';

const HeadersList = [
  {
    text: 'Approved',
    value: 'migrationPoll.checked',
    align: 'center',
    width: 100
  },
  {
    text: 'Name',
    value: 'rhapsody_name',
    align: 'center'
  },
  {
    text: 'Entry name',
    value: 'entry_variation_name',
    align: 'center'
  },
  {
    text: 'Notes',
    value: 'migrationPoll.notes',
    align: 'center',
    justify: 'flex-end'
  }
];
const GroupByHeadersList = [
  {
    text: 'Category',
    value: 'orig_cat1',
    align: 'center'
  },
  {
    text: 'Name',
    value: 'orig_name',
    align: 'center'
  },
  {
    text: 'Id',
    value: 'orig_code',
    align: 'center'
  },
  {
    text: 'Original external code',
    value: 'orig_external_code',
    align: 'center'
  },
  {
    text: 'Price',
    value: 'priceInfo.price',
    align: 'center'
  },
  {
    text: 'Last sold',
    value: 'priceInfo.lastsold',
    align: 'center'
  }
];

export default {
  props: {
    currentStep: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    StepsStatusesMap,
    HeadersList,
    GroupByHeadersList,
    resultId: null,
    rows: [],
    loading: false,
    pagination: {
      page: 1,
      pageCount: 2,
      pageSize: 200,
      rowCount: 0
    },
    statistics: {},
    snackbar: false,
    unresolvedFirst: true
  }),
  components: {
    VirtualizeTable,
    ResolveMultimatchDialog
  },
  async mounted() {
    this.resultId = this.$route.params.result_id;
    await this.getRows();
    await this.getStatistics();
  },
  methods: {
    async getRows() {
      this.loading = true;
      const isFetchAll = this.pagination.pageSize === 'All';
      const stepParams = {
        multimatch: DEFAULTS.MULTIMATCH_FIELDS,
        ...(this.unresolvedFirst
          ? { sortBy: ['multimatch_group_approved'] }
          : {})
      };
      const params = isFetchAll
        ? stepParams
        : {
            page: this.pagination.page,
            rowsPerPage: this.pagination.pageSize,
            ...stepParams
          };

      const { items, pagination } = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).fetchRows(this.resultId, params, isFetchAll);

      this.rows = items;

      if (pagination) {
        this.pagination = pagination;
      }

      this.loading = false;
    },
    async getStatistics() {
      this.statistics = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).getMultimatchStatistics(this.resultId, {
        multimatch: DEFAULTS.MULTIMATCH_FIELDS
      });
    },
    async handleChange(resultRowId, updated) {
      this.loading = true;

      const updatedRow = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).updateRow(this.$route.params.result_id, resultRowId, {
        migrationPoll: updated
      });

      _.merge(_.find(this.rows, { id: resultRowId }), updatedRow);

      if (this.currentStep.status !== StepsStatusesMap.IN_PROGRESS) {
        this.$emit('onUpdateStepStatus', StepsStatusesMap.IN_PROGRESS);
      }

      this.loading = false;
      this.snackbar = true;
    },
    async handleMultimatchResolveChange(resultRowId, resolved) {
      this.loading = true;

      const updatedRow = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? migrationApiV2
          : migrationApiV1
      ).updateMultimatchResolving(
        this.$route.params.result_id,
        resultRowId,
        resolved
      );

      _.merge(_.find(this.rows, { id: resultRowId }), updatedRow);

      if (this.currentStep.status !== StepsStatusesMap.IN_PROGRESS) {
        this.$emit('onUpdateStepStatus', StepsStatusesMap.IN_PROGRESS);
      }

      this.loading = false;
      this.snackbar = true;
    },
    openMultimatchResolveDialog(event, row) {
      event.preventDefault();
      this.$refs.resolveMultimatchDialog.open(row);
    },
    async handleMultimatchResolve(row) {
      const currentResolveState = _.get(row, 'migrationPoll.checked', false);

      await this.handleMultimatchResolveChange(row.id, !currentResolveState);
      await this.getRows();
      await this.getStatistics();
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.pagination.page = 1;
      this.pagination.pageSize = itemsPerPage;
      this.getRows();
    },
    handlePageChange(pageNum) {
      if (this.pagination.page !== pageNum) {
        this.pagination.page = pageNum;
        this.getRows();
      }
    }
  }
};
</script>
